import React from 'react'
import { useAtom } from 'jotai'
import Classnames from 'classnames'
import Sticky from 'react-sticky-el'
import { RiExternalLinkLine } from 'react-icons/ri'

// helper functions
import { returnValue } from '@/utils/op-types/helperFunctions'

// store
import {
  isBuilder,
  manifestStatic,
  pageContentStatic,
  manifestDesign,
  pageContentDesign,
  manifestBlocks,
  pageContentBlocks,
} from '@/utils/op-types/store'

// block renderer
import BlockRenderer from '@/components/optypes/frontend/BlockRenderer'
import OnepagerFooter from '@/components/optypes/headlesss-components/OnepagerFooter'

// block builder tools
import StaticContentWrapper from '@/components/optypes/frontend/StaticContentWrapper'
import AddBlockBuilder from '@/components/optypes/frontend/AddBlockBuilder'

// blocks
import Header from '@/components/optypes/frontend/StickyPage/blocks/Header'
import ButtonBlock from '@/components/optypes/frontend/StickyPage/blocks/ButtonBlock'
import LinkBlock from '@/components/optypes/frontend/StickyPage/blocks/LinkBlock'

const Components = {
  header: Header,
  ctablock: ButtonBlock,
  linkblock: LinkBlock,
}

const MainComponentRenderer = () => {
  const [builder] = useAtom(isBuilder)
  // manifest settings
  const [opStatic] = useAtom(manifestStatic)
  const [opDesign] = useAtom(manifestDesign)
  const [opBlocks] = useAtom(manifestBlocks)
  // custom page settings
  const [pageStatic] = useAtom(pageContentStatic)
  const [pageDesign] = useAtom(pageContentDesign)
  const [pageBlocks] = useAtom(pageContentBlocks)

  return (
    <div
      style={{
        backgroundColor: returnValue('main_background', opDesign, pageDesign),
        color: returnValue('text_color', opDesign, pageDesign),
      }}
    >
      <main>
        {/* header */}
        <div
          className="pt-16 pb-24 overflow-hidden relative"
          style={{
            backgroundColor: returnValue(
              'header_background',
              opDesign,
              pageDesign
            ),
            color: returnValue('header_text_color', opDesign, pageDesign),
          }}
        >
          {/* background image */}
          {returnValue('bgimgurl', opDesign, pageDesign) && (
            <div
              className="absolute top-0 left-0 z-0 h-full w-full  bg-cover bg-no-repeat"
              style={{
                backgroundImage: `url(${returnValue(
                  'bgimgurl',
                  opDesign,
                  pageDesign
                )})`,
                opacity: returnValue('bgimgopacity', opDesign, pageDesign),
              }}
            ></div>
          )}
          <div className="mx-auto" style={{ maxWidth: 1100 }}>
            <div className={Classnames('flex items-center', {})}>
              <div
                className={Classnames('w-1/2', {
                  'pl-12 order-last':
                    returnValue('stickylayout', opStatic, pageStatic) == 'left',
                  'pr-12':
                    returnValue('stickylayout', opStatic, pageStatic) ==
                    'right',
                })}
              >
                <h2 className="text-5xl font-bold mb-4">
                  <StaticContentWrapper contentKey="main_headline">
                    {returnValue('main_headline', opStatic, pageStatic)}
                  </StaticContentWrapper>
                </h2>
                <div className="text-lg">
                  <StaticContentWrapper contentKey="sub_headline">
                    {returnValue('sub_headline', opStatic, pageStatic)}
                  </StaticContentWrapper>
                </div>
              </div>

              <div
                className={Classnames('w-1/2', {
                  'pr-12':
                    returnValue('stickylayout', opStatic, pageStatic) == 'left',
                  'pl-12':
                    returnValue('stickylayout', opStatic, pageStatic) ==
                    'right',
                })}
              >
                <Sticky
                  topOffset={-50}
                  stickyStyle={{
                    transition: 'all 0.4s ease',
                    marginTop: 30,
                  }}
                >
                  <StaticContentWrapper contentKey="header_image">
                    <img
                      src={returnValue('header_image', opStatic, pageStatic)}
                      className="rounded-lg w-full shadow-xl"
                    />
                  </StaticContentWrapper>

                  {returnValue('main_button_show', opStatic, pageStatic) !=
                    'false' && (
                    <div className="py-6 fixed w-full stickypagebutton">
                      <StaticContentWrapper contentKey="main_button">
                        <button
                          onClick={() => {
                            location.href = returnValue(
                              'main_button_url',
                              opStatic,
                              pageStatic
                            )
                          }}
                          className="w-full rounded-xl font-bold text-center justify-center text-2xl flex items-center py-4 px-12"
                          style={{
                            backgroundColor: returnValue(
                              'buttonbgcolor',
                              opDesign,
                              pageDesign
                            ),
                            color: returnValue(
                              'buttontextcolor',
                              opDesign,
                              pageDesign
                            ),
                          }}
                        >
                          {returnValue('main_button', opStatic, pageStatic)}
                          <div className="ml-4">
                            <RiExternalLinkLine />
                          </div>
                        </button>
                      </StaticContentWrapper>
                      <StaticContentWrapper contentKey="main_button_subtitle">
                        <div
                          className="text-sm prose font-medium  py-4 px-8"
                          style={{
                            color: returnValue(
                              'smalltextcolor',
                              opDesign,
                              pageDesign
                            ),
                          }}
                          dangerouslySetInnerHTML={{
                            __html: returnValue(
                              'main_button_subtitle',
                              opStatic,
                              pageStatic
                            ),
                          }}
                        ></div>
                      </StaticContentWrapper>
                    </div>
                  )}
                  <div className="stickypagestart text-sm font-medium mt-4">
                    <StaticContentWrapper contentKey="stickycopystart">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: returnValue(
                            'stickycopystart',
                            opStatic,
                            pageStatic
                          ),
                        }}
                      ></div>
                    </StaticContentWrapper>
                  </div>
                </Sticky>
              </div>
            </div>
          </div>
        </div>

        {/* Blocks */}
        <div className="py-16 relative">
          <div className="mx-auto" style={{ maxWidth: 1100 }}>
            <div className="flex">
              <div
                className={Classnames('w-1/2 -mt-28', {
                  'pl-12 order-last':
                    returnValue('stickylayout', opStatic, pageStatic) == 'left',
                  'pr-12':
                    returnValue('stickylayout', opStatic, pageStatic) ==
                    'right',
                })}
              >
                {pageBlocks.map((block, index) => (
                  <div className="mb-6">
                    <BlockRenderer
                      key={index}
                      index={index}
                      Components={Components}
                      block={block}
                    />
                  </div>
                ))}

                {builder && <AddBlockBuilder btnText="Add Block" />}
              </div>
              <div className="w-1/2 px-12"></div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="py-12">
          <div className="mx-auto" style={{ maxWidth: 1100 }}>
            <div className="flex items-center justify-center">
              <OnepagerFooter />
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default MainComponentRenderer
